import { App as CapacitorApp } from "@capacitor/app";

type NativeUrlCallback = (url: string) => void;
type NativeUrlOpenEvent = CustomEvent<{ url?: string }>;

const subscribers = new Set<NativeUrlCallback>();
const pendingUrls: string[] = [];
const recentUrls = new Map<string, number>();
let capacitorListenerStarted = false;
let windowListenerStarted = false;

export function shouldProcessNativeUrl(url: string) {
  const now = Date.now();
  const previous = recentUrls.get(url);
  if (previous && now - previous < 2_000) return false;

  recentUrls.set(url, now);
  for (const [recentUrl, seenAt] of recentUrls) {
    if (now - seenAt > 10_000) recentUrls.delete(recentUrl);
  }

  return true;
}

export function subscribeNativeUrlOpen(callback: NativeUrlCallback) {
  startNativeUrlOpenListener();
  subscribers.add(callback);

  const queuedUrls = pendingUrls.splice(0);
  queuedUrls.forEach((url) => callback(url));

  return () => {
    subscribers.delete(callback);
  };
}

export async function getNativeLaunchUrl() {
  const event = await CapacitorApp.getLaunchUrl();
  return event?.url || null;
}

function startNativeUrlOpenListener() {
  if (!windowListenerStarted && typeof window !== "undefined") {
    window.addEventListener("flextechNativeUrlOpen", handleNativeUrlOpen);
    windowListenerStarted = true;
  }

  if (capacitorListenerStarted) return;
  capacitorListenerStarted = true;

  Promise.resolve(CapacitorApp.addListener("appUrlOpen", (event) => {
    if (event.url) publishNativeUrl(event.url);
  })).catch(() => {
    capacitorListenerStarted = false;
  });
}

function handleNativeUrlOpen(event: Event) {
  const url = (event as NativeUrlOpenEvent).detail?.url;
  if (url) publishNativeUrl(url);
}

function publishNativeUrl(url: string) {
  if (subscribers.size === 0) {
    pendingUrls.push(url);
    return;
  }

  subscribers.forEach((callback) => callback(url));
}

startNativeUrlOpenListener();
